import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import { useShiftsListStore } from '@libs/store/shifts';
import { Qualification } from '@libs/models/talents';
import { AvatarList, ShiftType } from '@organisms/dataDisplay';
import { DataGrid, GridColDef } from '@molecules/dataDisplay';
import { ShiftStatusChip } from '@atoms/dataDisplay';
import { CloseIcon } from '@atoms/icons';
import { Button } from '@atoms/buttons';
import { Flex } from '@atoms/layout';
import { AssignToMultipleShiftsDialog } from '../assignToMultipleShiftsDialog';
import { ShiftAssigneeButton } from '../singleShiftAssigneeButton';
import { UnassignDialog } from '../unassignDialog';
import { ShiftStatus } from '@libs/models/shifts/shift';
import { RecurrentShift } from '@libs/models/shifts';
import { Box } from '@mui/material';

type ShiftListProps = {
  list?: ShiftListItem[];
  loading: boolean;
  locationId?: string;
  onAssignClick?: (selectedShifts: string[], assignee: string, isSingle: boolean) => Promise<void>;
  onUnassignClick?: (selectedShift: string, reason: string, reasonComment: string) => Promise<void>;
  onShiftChanged?: () => void;
  parentShift: RecurrentShift | null;
};
export function ShiftsList({
  list = [],
  loading,
  onAssignClick,
  onShiftChanged,
  locationId,
  parentShift,
  onUnassignClick,
}: ShiftListProps) {
  const { allowedMultipleAssignees = true, qualifications, canAssign, canUnassign } = parentShift ?? {};
  const assignee = list.map((item) => item.assignee).filter((item) => item !== null);
  const uniqueAssignee = Array.from(new Set(assignee.map((item) => item?.id)))
    .map((id) => assignee.find((item) => item?.id === id))
    .filter((item) => item !== undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const [opedAssignDialog, setOpedAssignDialog] = useState(false);
  const [openUnassignDialog, setOpenUnassignDialog] = useState(false);
  const openShiftDrawer = useShiftsListStore((s) => s.openShiftDetails);
  const onShiftChangedInternal = () => {
    onShiftChanged?.();
    setSelected([]);
  };
  const shiftsToAssign = allowedMultipleAssignees ? selected : [parentShift?.id ?? ''];
  const isAssigned = assignee.length > 0;

  console.log('Parent Shift', parentShift);

  const shouldShowExternalAssignButton = parentShift?.assignRequest?.status === 'Pending';
  const shouldShowUnassignButton = isAssigned && !allowedMultipleAssignees && !shouldShowExternalAssignButton;
  const shouldShowAssignButton = !isAssigned && !shouldShowExternalAssignButton;

  return (
    <>
      <Flex
        column
        sx={{ height: '100%', width: '100%' }}
        px={3}
        pb={3}
        pt={2}
        bgcolor={(theme) => `color-mix(in srgb, #fff 100%, ${theme.palette.primary.main} 8%)`}
      >
        <Stack direction="row" alignItems={'center'} mb={2} gap={2}>
          {!shouldShowExternalAssignButton && (
            <>
              <Collapse in={!loading}>
                <Flex gap={1} alignItems="center">
                  {uniqueAssignee.length > 0 ? (
                    <AvatarList list={uniqueAssignee} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      <FormattedMessage id="shifts.noAssignees" />
                    </Typography>
                  )}
                </Flex>
              </Collapse>
              <Flex flex={1} />
              <Collapse in={!!selected.length}>
                <Flex alignCenter gap={1}>
                  <IconButton sx={{ flexShrink: 0 }} onClick={() => setSelected([])}>
                    <CloseIcon fontSize={14} width={14} />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      id="shifts.editSeriesDrawer.selectedShifts"
                      values={{ number: selected.length }}
                    />
                  </Typography>
                </Flex>
              </Collapse>
              <Box flex={1} />
            </>
          )}

          {!!parentShift && shouldShowExternalAssignButton && (
            <Box sx={{ height: '56px', width: '100%', p: 0 }}>
              <ShiftAssigneeButton
                shift={{
                  ...parentShift,
                  location: {
                    name: '',
                    address1: '',
                    city: '',
                    state: '',
                    address2: '',
                  },
                  specialty: parentShift.specialty?.title,
                  status: 'NotAllowed',
                  recurrence: parentShift.id.includes('series') ? 'series' : 'rotation',
                  recurrenceId: parentShift.id,
                  isArchive: parentShift.isArchived,
                  allowedMultipleAssignees: false,
                  externalAssignRequest: !!parentShift.assignRequest,
                  assignee: parentShift.assignees.length
                    ? { ...parentShift.assignees[0], assignedByClient: false }
                    : null,
                  shiftLength: dayjs(parentShift.endDate).diff(dayjs(parentShift.startDate), 'millisecond'),
                  shiftStart: dayjs(parentShift.startDate),
                }}
              />
            </Box>
          )}

          {shouldShowUnassignButton && (
            <Button
              variant="contained"
              onClick={() => setOpenUnassignDialog(true)}
              sx={{ px: 6, flexShrink: 0 }}
              disabled={!canUnassign}
              startIcon={<CloseIcon fontSize={14} width={14} color="white" />}
            >
              <FormattedMessage id="shifts.unassignBtnLabel" />
            </Button>
          )}
          {shouldShowAssignButton && (
            <Button
              variant="contained"
              onClick={() => setOpedAssignDialog(true)}
              sx={{ px: 6, flexShrink: 0 }}
              disabled={shiftsToAssign.length === 0 || !canAssign}
            >
              <FormattedMessage id="shifts.assignBtnLabel" />
            </Button>
          )}
        </Stack>
        <Flex height={'100%'} minHeight={0}>
          <DataGrid
            rows={list ?? []}
            paginationMode="client"
            sortingMode="client"
            pageSizeOptions={[10, 20]}
            loading={loading}
            rowHeight={64}
            columns={useShiftsListColumns({
              onAssigned: onShiftChangedInternal,
              onUnassigned: onShiftChangedInternal,
              allowedMultipleAssignees,
            })}
            checkboxSelection={allowedMultipleAssignees}
            checkboxSelectionVisibleOnly
            onRowSelectionModelChange={(params) => {
              setSelected(params.map((p) => p.toString()));
            }}
            onRowClick={(params) => {
              openShiftDrawer(`${params.id}`);
            }}
            isRowSelectable={({ row }) => !row.assignee && isShiftAllowedForAssignment(row)}
            rowSelectionModel={selected}
            disableRowSelectionOnClick
          />
        </Flex>
      </Flex>

      <AssignToMultipleShiftsDialog
        open={opedAssignDialog}
        shiftsNumber={allowedMultipleAssignees ? shiftsToAssign.length : list.length}
        onClose={() => setOpedAssignDialog(false)}
        onSubmit={async (v) => {
          await onAssignClick?.(shiftsToAssign, v, !allowedMultipleAssignees);
          setOpedAssignDialog(false);
          setSelected([]);
        }}
        loading={loading}
        locationId={locationId}
        qualifications={qualifications?.map((q) => q.id)}
        warningMessage={
          allowedMultipleAssignees ? undefined : <FormattedMessage id="shifts.assignDialog.singleAssigneeDisclaimer" />
        }
      />

      {parentShift && (
        <UnassignDialog
          open={openUnassignDialog}
          shift={parentShift}
          onSubmit={async (reason, reasonComment) => {
            await onUnassignClick?.(parentShift.id, reason, reasonComment);
            setOpenUnassignDialog(false);
          }}
          loading={loading}
          onClose={() => setOpenUnassignDialog(false)}
        />
      )}
    </>
  );
}

function useShiftsListColumns({
  onAssigned,
  onUnassigned,
  allowedMultipleAssignees,
}: {
  onAssigned?: () => void;
  onUnassigned?: () => void;
  allowedMultipleAssignees: boolean;
}) {
  const intl = useIntl();

  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<ShiftListItem>[] = [getDateColumn(intl), getShiftTypeColumn(intl), getStatusColumn(intl)];

    if (allowedMultipleAssignees) {
      columns.push(getAssigneeColumn(intl, { onAssigned, onUnassigned }));
    }
    return columns;
  }, [intl, onAssigned, onUnassigned, allowedMultipleAssignees]);
}

function getDateColumn(intl: IntlShape): GridColDef<ShiftListItem> {
  return {
    field: 'startDate',
    headerName: intl.formatMessage({
      id: 'shifts.fields.date',
    }),
    flex: 1,
    minWidth: 80,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      const row = params.row;
      const formattedStart = dayjs(row.startDate).format('MM.DD.YYYY');
      const weekday = dayjs(row.startDate).format('dddd');
      return (
        <Stack direction={'column'} justifyContent={'center'} height={'100%'}>
          <Typography variant="body2">{formattedStart}</Typography>
          <Typography variant="caption">{weekday}</Typography>
        </Stack>
      );
    },
  };
}

function getShiftTypeColumn(intl: IntlShape): GridColDef<ShiftListItem> {
  return {
    field: 'shiftStart',
    headerName: intl.formatMessage({
      id: 'shifts.fields.shiftType',
    }),
    flex: 1,
    minWidth: 160,
    renderCell: (params) => {
      return (
        <ShiftType
          startTime={dayjs(params.row.startDate)}
          length={dayjs(params.row.endDate).diff(dayjs(params.row.startDate), 'millisecond')}
          boxProps={{ height: '100%' }}
        />
      );
    },
  };
}

function getStatusColumn(intl: IntlShape): GridColDef<ShiftListItem> {
  return {
    field: 'status',
    headerName: intl.formatMessage({ id: 'shifts.fields.status' }),
    flex: 1,
    minWidth: 80,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }

      return <ShiftStatusChip value={params.value} />;
    },
  };
}

function getAssigneeColumn(
  intl: IntlShape,
  {
    onAssigned,
    onUnassigned,
  }: {
    onAssigned?: () => void;
    onUnassigned?: () => void;
  },
): GridColDef<ShiftListItem, ShiftListItem['assignee']> {
  return {
    field: 'assignee',
    headerName: intl.formatMessage({ id: 'shifts.fields.assignee' }),
    flex: 1,
    // To prevent assignee column component to collapse
    minWidth: 165,
    sortComparator: (v1, v2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1.name}`.localeCompare(`${v1.name}`);
    },
    renderCell: ({ row }) => {
      const disableUnassign = ['InProgress', 'Completed', 'Canceled'].includes(row.status);
      const allowAssign = isShiftAllowedForAssignment(row);
      return (
        // To align assigned and unassigned shifts
        <Box pl={row.assignee ? 1 : 0}>
          <ShiftAssigneeButton
            shift={{
              ...row,
              id: row.id,
              externalId: '',
              assignRequest: null,
              notes: [],
              location: {
                name: '',
                address1: '',
                city: '',
                state: '',
                address2: '',
              },
              isArchive: false,
              tags: [],
              status: row.status as ShiftStatus,
              allowedMultipleAssignees: false,
              externalAssignRequest: false,
              isUrgent: false,
              assignee: row.assignee ? { ...row.assignee, assignedByClient: false } : null,
              assignees: row.assignee ? [{ ...row.assignee, assignedByClient: false }] : [],
              qualifications: row.qualifications,
              shiftLength: dayjs(row.endDate).diff(dayjs(row.startDate), 'millisecond'),
              shiftStart: dayjs(row.startDate).tz(row.timeZone),
            }}
            disabled={row.assignee ? disableUnassign : !allowAssign}
            onAssigned={onAssigned}
            onUnassigned={onUnassigned}
          />
        </Box>
      );
    },
  };
}

type ShiftListItem = {
  id: string;
  startDate: string;
  endDate: string;
  recurrence: 'single' | 'rotation' | 'series';
  recurrenceId: string;
  timeZone: string;
  status: string;
  assignee: {
    id: string;
    name: string;
    avatar: string;
  } | null;
  qualifications: Qualification[];
};

function isShiftAllowedForAssignment(d: { status: string }): boolean {
  return ['Unassigned', 'Pending', 'Opened'].includes(d.status);
}
